import { useEffect, useRef } from 'react';
import { Renderer2D, paragraphFormats } from '@vellum/core';
import { app } from '../app.ts';
import { GLRenderer } from '../gl/renderer.ts';
import { OverlayRenderer } from './overlay.ts';
import { Interaction, insertImage } from './interaction.ts';
import { TextEditor } from './TextEditor.tsx';
import { TitleRename } from './TitleRename.tsx';
import { EmbedPlayer } from './EmbedPlayer.tsx';
import { useApp } from '../hooks.ts';

/** Fallback when WebGL2 is unavailable. */
class Canvas2DContent {
  private r: Renderer2D | null = null;
  hidden = new Set<string>();
  constructor(private canvas: HTMLCanvasElement) {}
  resize(w: number, h: number, dpr: number) {
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
  }
  render() {
    const ctx = this.canvas.getContext('2d')!;
    if (!this.r || this.r.doc !== app.doc) this.r = new Renderer2D(app.doc);
    const dpr = window.devicePixelRatio || 1;
    const v = app.viewMatrix();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.r.renderPage(ctx, app.editor.pageId, {
      viewport: app.viewportRect(),
      transform: [v[0] * dpr, v[1] * dpr, v[2] * dpr, v[3] * dpr, v[4] * dpr, v[5] * dpr],
      images: app.images,
      shaders: app.shaders,
      hidden: this.hidden,
      lodThreshold: 1,
    });
    return false;
  }
  invalidate() {}
}

export let interaction: Interaction | null = null;
export let renderer: GLRenderer | Canvas2DContent | null = null;

export function CanvasView() {
  const host = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLCanvasElement>(null);
  const overlay = useRef<HTMLCanvasElement>(null);
  const editing = useApp('ui', () => app.ui.editingTextId, { sync: true });
  const renaming = useApp('ui', () => (app.ui.renaming?.on === 'canvas' ? app.ui.renaming.id : null), { sync: true });
  const playing = useApp('ui', () => app.ui.playingEmbedId, { sync: true });
  const outlineMode = useApp('ui', () => app.ui.outlineMode);

  useEffect(() => {
    const el = host.current!;
    const contentCanvas = content.current!;
    const overlayCanvas = overlay.current!;
    let r: GLRenderer | Canvas2DContent;
    try {
      if (new URLSearchParams(location.search).get('renderer') === '2d') throw new Error('2d requested');
      r = new GLRenderer(contentCanvas, app);
      app.ui.renderer = 'webgl';
    } catch (e) {
      console.warn('WebGL2 renderer unavailable, using Canvas2D:', (e as Error).message);
      // A canvas that already has a WebGL context can't give a 2D one: swap in a fresh element.
      const fresh = document.createElement('canvas');
      fresh.className = contentCanvas.className;
      contentCanvas.replaceWith(fresh);
      content.current = fresh;
      r = new Canvas2DContent(fresh);
      app.ui.renderer = 'canvas2d';
    }
    renderer = r;
    (window as unknown as { __vellumRenderer: unknown }).__vellumRenderer = r;
    // ?stats shows these next to the frame rate (see Fps)
    (window as unknown as { __vellumStats: () => string }).__vellumStats = () => {
      const s = (r as { stats?: { instances: number; meshTriangles: number; batches: number; buildMs: number; patchMs: number; pendingRasters: number } }).stats;
      return s ? `· ${s.instances} layers drawn · ${s.meshTriangles} triangles · ${s.batches} batches · last build ${s.buildMs.toFixed(0)} ms · patch ${s.patchMs.toFixed(1)} ms${s.pendingRasters ? ` · ${s.pendingRasters} rasters pending` : ''}` : '';
    };
    const ov = new OverlayRenderer(app);
    const octx = overlayCanvas.getContext('2d')!;
    const inter = new Interaction(app, el);
    interaction = inter;

    let raf = 0;
    let firstDrawn = false;
    let scheduled = false;
    let frames = 0;
    let fpsT = performance.now();
    const frame = () => {
      scheduled = false;
      raf = 0;
      const dpr = window.devicePixelRatio || 1;
      const editingNode = app.ui.editingTextId ? app.doc.get(app.ui.editingTextId) : null;
      // rich text stays rendered on canvas while editing (the textarea is a transparent caret layer)
      r.hidden = new Set(editingNode && !editingNode.textRuns?.length && !paragraphFormats(editingNode) && !editingNode.textPath && !editingNode.openTypeFeatures ? [editingNode.id] : []);
      const more = r.render();
      // when someone first sees their file (for measuring how long opening takes)
      if (!firstDrawn && app.fileId && app.doc.children(app.editor.pageId).length) {
        firstDrawn = true;
        performance.mark('vellum:first-frame');
      }
      ov.render(octx, dpr);
      frames++;
      const now = performance.now();
      if (now - fpsT > 1000) {
        app.ui.fps = Math.round((frames * 1000) / (now - fpsT));
        frames = 0;
        fpsT = now;
        app.emit('ui');
      }
      if (more) schedule();
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      raf = requestAnimationFrame(frame);
    };
    const offRender = app.onRender(schedule);

    const resize = () => {
      const rect = el.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      app.viewportSize = { width: rect.width, height: rect.height };
      r.resize(rect.width, rect.height, dpr);
      overlayCanvas.width = Math.round(rect.width * dpr);
      overlayCanvas.height = Math.round(rect.height * dpr);
      const cc = content.current!;
      cc.style.width = overlayCanvas.style.width = `${rect.width}px`;
      cc.style.height = overlayCanvas.style.height = `${rect.height}px`;
      schedule();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    resize();

    // guide drags that start on a ruler continue over the canvas
    const winMove = (e: PointerEvent) => {
      if (app.ui.draggingGuide) inter.pointerMove(e);
    };
    const winUp = (e: PointerEvent) => {
      if (app.ui.draggingGuide) inter.pointerUp(e);
    };
    window.addEventListener('pointermove', winMove);
    window.addEventListener('pointerup', winUp);
    // Two-finger touch: pinch to zoom, drag to pan (takes over from any single-finger gesture)
    const touches = new Map<number, { x: number; y: number }>();
    let pinch: { d0: number; mid0: { x: number; y: number }; cam: { x: number; y: number; zoom: number } } | null = null;
    const localPt = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const pinchState = () => {
      const [a, b] = [...touches.values()];
      return { d: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
    };
    const touchDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return false;
      touches.set(e.pointerId, localPt(e));
      if (touches.size === 2) {
        inter.cancel();
        const st = pinchState();
        pinch = { d0: st.d || 1, mid0: st.mid, cam: { ...app.camera } };
        return true;
      }
      return touches.size > 2;
    };
    const touchMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !touches.has(e.pointerId)) return false;
      touches.set(e.pointerId, localPt(e));
      if (!pinch || touches.size < 2) return false;
      const st = pinchState();
      const z = Math.max(0.01, Math.min(256, pinch.cam.zoom * (st.d / pinch.d0)));
      // keep the page point under the initial midpoint under the current midpoint
      const px = (pinch.mid0.x - pinch.cam.x) / pinch.cam.zoom,
        py = (pinch.mid0.y - pinch.cam.y) / pinch.cam.zoom;
      app.setCamera({ zoom: z, x: st.mid.x - px * z, y: st.mid.y - py * z });
      return true;
    };
    const touchUp = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return false;
      touches.delete(e.pointerId);
      if (pinch) {
        if (touches.size < 2) pinch = null;
        return true;
      }
      return false;
    };
    const down = (e: PointerEvent) => {
      // the text editor and its format bar handle their own pointer input
      if (e.target instanceof Element && e.target.closest('.text-editor, .format-bar')) return;
      if (touchDown(e)) return;
      // The canvas manages focus itself: blur panel inputs, and stop the browser's mousedown
      // focus handling from stealing focus from editors opened during this gesture.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body && !active.classList.contains('text-editor')) active.blur();
      if (e.button !== 1) e.preventDefault();
      inter.pointerDown(e);
    };
    const move = (e: PointerEvent) => {
      if (touchMove(e)) return;
      if (!app.ui.draggingGuide) inter.pointerMove(e);
    };
    const up = (e: PointerEvent) => {
      if (touchUp(e)) return;
      if (!app.ui.draggingGuide) inter.pointerUp(e);
    };
    const wheel = (e: WheelEvent) => inter.wheel(e);
    const leave = () => app.sync?.presence({ cursor: null });
    const ctxmenu = (e: MouseEvent) => {
      e.preventDefault();
      inter.contextSelect(e);
      app.setUI({ contextMenu: { x: e.clientX, y: e.clientY } });
    };
    const drop = async (e: DragEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const p = app.screenToPage({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      for (const f of Array.from(e.dataTransfer?.files ?? [])) if (f.type.startsWith('image/') || f.type.startsWith('video/')) await insertImage(app, f, p);
    };
    const dragover = (e: DragEvent) => e.preventDefault();
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('pointerleave', leave);
    el.addEventListener('wheel', wheel, { passive: false });
    el.addEventListener('contextmenu', ctxmenu);
    el.addEventListener('drop', drop);
    el.addEventListener('dragover', dragover);
    // Safari pinch
    const gesture = (e: Event) => e.preventDefault();
    el.addEventListener('gesturestart', gesture);
    return () => {
      cancelAnimationFrame(raf);
      offRender();
      window.removeEventListener('pointermove', winMove);
      window.removeEventListener('pointerup', winUp);
      ro.disconnect();
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('pointerleave', leave);
      el.removeEventListener('wheel', wheel);
      el.removeEventListener('contextmenu', ctxmenu);
      el.removeEventListener('drop', drop);
      el.removeEventListener('dragover', dragover);
      el.removeEventListener('gesturestart', gesture);
      if (r instanceof GLRenderer) r.dispose();
      interaction = null;
      renderer = null;
    };
  }, []);

  return (
    <div ref={host} className="canvas-host" data-testid="canvas">
      <canvas ref={content} className="canvas-layer" style={{ opacity: outlineMode ? 0 : 1 }} />
      <canvas ref={overlay} className="canvas-layer overlay" />
      {editing && !app.readOnly && <TextEditor id={editing} key={editing} />}
      {renaming && !app.readOnly && <TitleRename id={renaming} key={renaming} />}
      {playing && <EmbedPlayer id={playing} key={playing} />}
    </div>
  );
}
